import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, hasConfig } from '../auth/firebaseClient';

const SETTINGS_KEY = 'vf_admin_settings';
const BUDGET_CACHE_KEY = 'vf_budget_cache';

const DEFAULT_BUDGET_SETTINGS = {
  cloudinary: {
    enabled: true,
    limit: 10,
    currency: 'USD',
    alertAt: 0.8,
  },
  mux: {
    enabled: true,
    limit: 5,
    currency: 'USD',
    alertAt: 0.8,
  },
  vercel: {
    enabled: true,
    limit: 5,
    currency: 'USD',
    alertAt: 0.8,
  },
  general: {
    totalBudget: 20,
    currency: 'USD',
    alertAt: 0.8,
    dailyAutoRefresh: false,
    lastRefresh: null,
  },
};

const getLocalCache = () => {
  try {
    const cached = localStorage.getItem(BUDGET_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error('Error reading budget cache:', e);
  }
  return null;
};

const setLocalCache = (data) => {
  try {
    localStorage.setItem(BUDGET_CACHE_KEY, JSON.stringify({
      ...data,
      cachedAt: Date.now(),
    }));
  } catch (e) {
    console.error('Error writing budget cache:', e);
  }
};

const getSettingsDoc = () => {
  if (!hasConfig || !db) return null;
  return doc(db, 'admin', 'settings');
};

export const getBudgetSettings = async () => {
  const local = getLocalCache();
  if (local?.settings) {
    return local.settings;
  }
  
  if (!hasConfig || !db) {
    return DEFAULT_BUDGET_SETTINGS;
  }
  
  try {
    const docSnap = await getDoc(getSettingsDoc());
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.budget || DEFAULT_BUDGET_SETTINGS;
    }
  } catch (e) {
    console.error('Error fetching budget settings:', e);
  }
  
  return DEFAULT_BUDGET_SETTINGS;
};

export const saveBudgetSettings = async (settings) => {
  if (!hasConfig || !db) {
    console.warn('Firestore not configured, saving to local only');
    setLocalCache({ settings, cachedAt: Date.now() });
    return;
  }
  
  try {
    await setDoc(getSettingsDoc(), {
      budget: settings,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    
    setLocalCache({ settings, cachedAt: Date.now() });
  } catch (e) {
    console.error('Error saving budget settings:', e);
    throw e;
  }
};

export const updateBudgetUsage = async (usage) => {
  const settings = await getBudgetSettings();
  const currentCache = getLocalCache() || {};
  
  const newCache = {
    settings,
    usage: {
      cloudinary: {
        storage: usage.cloudinary?.storage || currentCache.usage?.cloudinary?.storage || 0,
        bandwidth: usage.cloudinary?.bandwidth || currentCache.usage?.cloudinary?.bandwidth || 0,
        apiCalls: usage.cloudinary?.apiCalls || currentCache.usage?.cloudinary?.apiCalls || 0,
      },
      mux: {
        minutes: usage.mux?.minutes || currentCache.usage?.mux?.minutes || 0,
        storage: usage.mux?.storage || currentCache.usage?.mux?.storage || 0,
      },
      vercel: {
        bandwidth: usage.vercel?.bandwidth || currentCache.usage?.vercel?.bandwidth || 0,
        builds: usage.vercel?.builds || currentCache.usage?.vercel?.builds || 0,
      },
    },
    cachedAt: Date.now(),
  };
  
  setLocalCache(newCache);
  
  if (hasConfig && db) {
    try {
      await setDoc(getSettingsDoc(), {
        budgetUsage: newCache.usage,
        budgetLastUpdated: serverTimestamp(),
      }, { merge: true });
    } catch (e) {
      console.error('Error saving budget usage to Firestore:', e);
    }
  }
  
  return newCache;
};

export const getBudgetStatus = async () => {
  const settings = await getBudgetSettings();
  const cache = getLocalCache();
  const usage = cache?.usage || {};
  
  const calculateStatus = (serviceSettings, serviceUsage) => {
    if (!serviceSettings?.enabled) return { enabled: false, used: 0, limit: 0, percent: 0, alert: false };
    
    let used = 0;
    let limit = serviceSettings.limit || 0;
    
    if (serviceSettings.limit === 0) {
      limit = settings.general?.totalBudget || 20;
    }
    
    if (serviceSettings === settings.cloudinary) {
      const storageCost = (serviceUsage?.storage || 0) * 0.004;
      const bandwidthCost = (serviceUsage?.bandwidth || 0) * 0.20;
      used = storageCost + bandwidthCost;
    } else if (serviceSettings === settings.mux) {
      used = (serviceUsage?.minutes || 0) * 0.002;
    } else if (serviceSettings === settings.vercel) {
      used = (serviceUsage?.bandwidth || 0) * 0.02 + (serviceUsage?.builds || 0) * 0.01;
    }
    
    const percent = limit > 0 ? (used / limit) : 0;
    const alertAt = serviceSettings.alertAt || 0.8;
    
    return {
      enabled: true,
      used: Math.round(used * 100) / 100,
      limit: Math.round(limit * 100) / 100,
      percent: Math.round(percent * 100),
      alert: percent >= alertAt,
      overBudget: percent > 1,
    };
  };
  
  return {
    cloudinary: calculateStatus(settings.cloudinary, usage.cloudinary),
    mux: calculateStatus(settings.mux, usage.mux),
    vercel: calculateStatus(settings.vercel, usage.vercel),
    general: {
      totalBudget: settings.general?.totalBudget || 20,
      alertAt: settings.general?.alertAt || 0.8,
      dailyAutoRefresh: settings.general?.dailyAutoRefresh || false,
      lastRefresh: cache?.cachedAt || null,
    },
  };
};

export const shouldAutoRefresh = async () => {
  const settings = await getBudgetSettings();
  const cache = getLocalCache();
  
  if (!settings.general?.dailyAutoRefresh) return false;
  
  const lastRefresh = cache?.cachedAt;
  if (!lastRefresh) return true;
  
  const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
  return lastRefresh < dayAgo;
};

export const clearBudgetCache = () => {
  localStorage.removeItem(BUDGET_CACHE_KEY);
};

export { DEFAULT_BUDGET_SETTINGS };